import { create } from "zustand";
import {
  AUTO_DRIP_MS,
  GROUND_Y,
  MAX_CUBES,
  MOBILE_MAX_CUBES,
  PILE_SNAPSHOT_CAP,
  SAVE_KEY,
  STREAK_HITS,
  STREAK_MULT_BONUS,
  STREAK_WINDOW_MS,
  SURGE_COST,
  SURGE_DURATION_MS,
  SURGE_SPAWN_BONUS,
  UPGRADES,
} from "../config/game";
import { createFragments, resetSpawnIds } from "../systems/spawn";
import {
  playClick,
  playUnlock,
  setMuted,
  unlockAudio,
} from "../systems/audio";

function isMobileCap() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 768px), (pointer: coarse)").matches;
}

function cubeCap() {
  return isMobileCap() ? MOBILE_MAX_CUBES : MAX_CUBES;
}

function nextPendingUpgrade(unlockedIds, clickCount) {
  return (
    UPGRADES.find(
      (u) => clickCount >= u.threshold && !unlockedIds.includes(u.id)
    ) ?? null
  );
}

function serializeCubes(cubes) {
  return cubes.slice(-PILE_SNAPSHOT_CAP).map((c) => ({
    p: c.position.map((n) => Math.round(n * 100) / 100),
    s: Math.round(c.scale * 1000) / 1000,
    v: c.variant === "compact" || c.variant === "dense" ? c.variant : "s",
    m: c.mass ?? 0.4,
    r: c.restitution ?? 0.12,
  }));
}

function hydrateCubes(snapshot) {
  if (!Array.isArray(snapshot) || !snapshot.length) return [];
  return snapshot.slice(0, PILE_SNAPSHOT_CAP).map((c, i) => {
    const variant =
      c.v === "compact" ? "compact" : c.v === "dense" ? "dense" : "standard";
    return {
      id: `restored-${i}`,
      position: Array.isArray(c.p) ? c.p : [0, GROUND_Y + 0.2, 0],
      linearVelocity: [0, 0, 0],
      angularVelocity: [0, 0, 0],
      scale: typeof c.s === "number" ? c.s : 0.28,
      createdAt: 0,
      variant,
      mass: typeof c.m === "number" ? c.m : variant === "compact" ? 0.7 : 0.4,
      restitution:
        typeof c.r === "number" ? c.r : variant === "compact" ? 0.05 : 0.12,
    };
  });
}

/**
 * Rebuild a representative resting pile when a save has no snapshot.
 * Deterministic enough to feel like “your Heap” without unbounded storage.
 */
function rebuildRepresentativePile(clickCount, unlockedIds) {
  const n = Math.min(
    PILE_SNAPSHOT_CAP,
    Math.max(0, Math.floor(clickCount * 0.12) + unlockedIds.length * 3)
  );
  if (n <= 0) return [];
  const denser = unlockedIds.includes("cascade");
  const compact = unlockedIds.includes("vein");
  const fragments = createFragments(n, { denser, compact });
  return fragments.map((f, i) => {
    const angle = (i / n) * Math.PI * 2;
    const radius = 0.35 + (i % 5) * 0.22;
    return {
      ...f,
      id: `rebuild-${i}`,
      position: [
        Math.cos(angle) * radius,
        GROUND_Y + 0.35 + (i % 4) * 0.28,
        Math.sin(angle) * radius,
      ],
      linearVelocity: [0, 0, 0],
      angularVelocity: [0, 0, 0],
    };
  });
}

function loadSave() {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function persist(slice) {
  try {
    localStorage.setItem(
      SAVE_KEY,
      JSON.stringify({
        score: slice.score,
        clickCount: slice.clickCount,
        multiplier: slice.multiplier,
        spawnCount: slice.spawnCount,
        autoDrip: slice.autoDrip,
        autoDripMs: slice.autoDripMs,
        dripUsesSpawnCount: slice.dripUsesSpawnCount,
        compactFragments: slice.compactFragments,
        unlockedIds: slice.unlockedIds,
        muted: slice.muted,
        surgeResolved: slice.surgeResolved,
        pile: serializeCubes(slice.cubes),
      })
    );
  } catch {
    /* private mode */
  }
}

const saved = typeof window !== "undefined" ? loadSave() : null;
const savedUnlocks = saved?.unlockedIds ?? [];

function initialCubes() {
  if (!saved) return [];
  if (Array.isArray(saved.pile) && saved.pile.length) {
    return hydrateCubes(saved.pile);
  }
  // Older saves may omit pile — rebuild a capped representative Heap.
  if (!saved.pile && (saved.clickCount > 0 || savedUnlocks.length)) {
    return rebuildRepresentativePile(saved.clickCount ?? 0, savedUnlocks);
  }
  return [];
}

const initialCubesList = initialCubes();
if (typeof window !== "undefined" && saved?.muted) {
  setMuted(true);
}

export const useGameStore = create((set, get) => ({
  phase: "intro", // intro | play | heap
  score: saved?.score ?? 0,
  clickCount: saved?.clickCount ?? 0,
  multiplier: saved?.multiplier ?? 1,
  spawnCount: saved?.spawnCount ?? 1,
  autoDrip: saved?.autoDrip ?? false,
  autoDripMs:
    saved?.autoDripMs ??
    (savedUnlocks.includes("vein") ? 1400 : AUTO_DRIP_MS),
  dripUsesSpawnCount:
    saved?.dripUsesSpawnCount ?? savedUnlocks.includes("cascade"),
  compactFragments:
    saved?.compactFragments ?? savedUnlocks.includes("vein"),
  unlockedIds: savedUnlocks,
  cubes: initialCubesList,
  pendingUpgrade: null,
  climaxActive: false,
  climaxArmedAt: 0,
  boostOfferActive: false,
  boostArmedAt: 0,
  surgeResolved: saved?.surgeResolved ?? false,
  surgeUntil: 0,
  surgeSpawnBonus: 0,
  strikeTimes: [],
  lastGain: 0,
  pulseToken: 0,
  muted: saved?.muted ?? false,
  reducedMotion: false,
  coarsePointer: false,

  setPrefs: ({ reducedMotion, coarsePointer }) =>
    set({ reducedMotion, coarsePointer }),

  setMutedPref: (muted) => {
    setMuted(muted);
    set({ muted });
    persist({ ...get(), muted });
  },

  start: () => {
    unlockAudio();
    set({ phase: "play" });
  },

  toggleHeap: () => {
    const { phase } = get();
    if (phase === "intro") return;
    set({ phase: phase === "heap" ? "play" : "heap" });
  },

  returnToPlay: () => {
    if (get().phase !== "intro") set({ phase: "play" });
  },

  dismissClimax: () => {
    const { climaxActive, climaxArmedAt, pendingUpgrade, surgeResolved } =
      get();
    if (climaxActive && performance.now() - climaxArmedAt < 650) return;

    const offerBoost =
      pendingUpgrade?.id === "core" && !surgeResolved;

    set({
      climaxActive: false,
      pendingUpgrade: null,
      climaxArmedAt: 0,
      ...(offerBoost
        ? { boostOfferActive: true, boostArmedAt: performance.now() }
        : {}),
    });
  },

  dismissBoostOffer: () => {
    const { boostOfferActive, boostArmedAt } = get();
    if (boostOfferActive && performance.now() - boostArmedAt < 650) return;
    set({
      boostOfferActive: false,
      boostArmedAt: 0,
      surgeResolved: true,
    });
    persist({ ...get() });
  },

  acceptBoost: () => {
    const state = get();
    if (!state.boostOfferActive) return;
    if (performance.now() - state.boostArmedAt < 650) return;
    if (state.score < SURGE_COST) {
      set({
        boostOfferActive: false,
        boostArmedAt: 0,
        surgeResolved: true,
      });
      persist({ ...get() });
      return;
    }

    set({
      score: state.score - SURGE_COST,
      surgeUntil: performance.now() + SURGE_DURATION_MS,
      surgeSpawnBonus: SURGE_SPAWN_BONUS,
      boostOfferActive: false,
      boostArmedAt: 0,
      surgeResolved: true,
      lastGain: -SURGE_COST,
      pulseToken: state.pulseToken + 1,
    });
    persist({ ...get() });
  },

  strike: () => {
    const state = get();
    if (state.phase === "intro" || state.climaxActive || state.boostOfferActive)
      return;

    unlockAudio();

    const now = performance.now();
    const strikeTimes = [...state.strikeTimes, now].filter(
      (t) => now - t < STREAK_WINDOW_MS
    );
    const onStreak = strikeTimes.length >= STREAK_HITS;

    playClick(onStreak ? "streak" : state.clickCount);

    const surgeOn = state.surgeUntil > now;
    const spawnN =
      state.spawnCount + (surgeOn ? state.surgeSpawnBonus || SURGE_SPAWN_BONUS : 0);

    const fragments = createFragments(spawnN, {
      denser: state.unlockedIds.includes("cascade"),
      compact: state.compactFragments,
    });

    const clickCount = state.clickCount + 1;
    const gain =
      1 * (state.multiplier + (onStreak ? STREAK_MULT_BONUS : 0));
    const score = state.score + gain;
    const cap = cubeCap();
    let cubes = [...state.cubes, ...fragments];
    if (cubes.length > cap) {
      cubes = cubes.slice(cubes.length - cap);
    }

    const pending = nextPendingUpgrade(state.unlockedIds, clickCount);
    let patch = {
      cubes,
      clickCount,
      score,
      strikeTimes,
      lastGain: gain,
      pulseToken: state.pulseToken + (onStreak ? 2 : 1),
    };

    if (pending && !state.climaxActive) {
      const applied = pending.apply();
      const unlockedIds = [...state.unlockedIds, pending.id];
      playUnlock();
      patch = {
        ...patch,
        ...applied,
        unlockedIds,
        pendingUpgrade: pending,
        climaxActive: true,
        climaxArmedAt: performance.now(),
      };
    }

    set(patch);
    persist({ ...get() });
  },

  removeCube: (id) => {
    set((s) => ({ cubes: s.cubes.filter((c) => c.id !== id) }));
  },

  /** Auto-drip after CORE unlock - same spawn path, quieter score tick. */
  drip: () => {
    const state = get();
    if (
      !state.autoDrip ||
      state.phase === "intro" ||
      state.climaxActive ||
      state.boostOfferActive
    )
      return;

    const now = performance.now();
    const surgeOn = state.surgeUntil > now;
    const baseCount = state.dripUsesSpawnCount ? state.spawnCount : 1;
    const count =
      baseCount + (surgeOn ? state.surgeSpawnBonus || SURGE_SPAWN_BONUS : 0);

    const fragments = createFragments(count, {
      denser: true,
      compact: state.compactFragments,
    });
    const cap = cubeCap();
    let cubes = [...state.cubes, ...fragments];
    if (cubes.length > cap) cubes = cubes.slice(cubes.length - cap);

    const gain = state.multiplier;
    set({
      cubes,
      score: state.score + gain,
      lastGain: gain,
      pulseToken: state.pulseToken + 1,
    });
    persist({ ...get() });
  },
}));

export function getAutoDripMs() {
  return useGameStore.getState().autoDripMs ?? AUTO_DRIP_MS;
}

export function hardResetProgress() {
  resetSpawnIds();
  try {
    localStorage.removeItem(SAVE_KEY);
  } catch {
    /* private mode */
  }
  setMuted(false);
  useGameStore.setState({
    phase: "intro",
    score: 0,
    clickCount: 0,
    multiplier: 1,
    spawnCount: 1,
    autoDrip: false,
    autoDripMs: AUTO_DRIP_MS,
    dripUsesSpawnCount: false,
    compactFragments: false,
    unlockedIds: [],
    cubes: [],
    pendingUpgrade: null,
    climaxActive: false,
    climaxArmedAt: 0,
    boostOfferActive: false,
    boostArmedAt: 0,
    surgeResolved: false,
    surgeUntil: 0,
    surgeSpawnBonus: 0,
    strikeTimes: [],
    lastGain: 0,
    pulseToken: 0,
    muted: false,
  });
}
